import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  CheckCircle,
  Cancel,
  Refresh,
  TrendingUp,
  Pending,
  Error,
  AccountBalanceWallet,
  Payment,
  ExpandMore,
  ThumbUp,
  ThumbDown,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchWithdrawals,
  updateWithdrawalStatus,
  setFilters,
  clearFilters,
  setPage,
} from '../store/slices/withdrawalsSlice';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Withdrawal, BankType } from '../types';

const WithdrawalsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { withdrawals, pagination, statistics, loading, error } = useSelector(
    (state: RootState) => state.withdrawals
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [bankTypeFilter, setBankTypeFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [showWithdrawalDetails, setShowWithdrawalDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchWithdrawals({ page: 1, limit: 10 }) as any);
  }, [dispatch]);

  const handleSearch = () => {
    const filters: any = {};
    if (searchTerm) {
      // Check if search term looks like a phone number
      if (searchTerm.startsWith('251')) {
        filters.phoneNumber = searchTerm;
      } else {
        filters.search = searchTerm;
      }
    }
    if (statusFilter) filters.status = statusFilter;
    if (bankTypeFilter) filters.bankType = bankTypeFilter;
    
    dispatch(setFilters(filters));
    dispatch(fetchWithdrawals({ page: 1, limit: 10, filters }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(fetchWithdrawals({ page: newPage + 1, limit: 10 }) as any);
  };

  const handleViewWithdrawal = (withdrawal: Withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowWithdrawalDetails(true);
  };

  const handleUpdateStatus = async (withdrawalId: string, status: string) => {
    const adminNote = window.prompt(`Enter admin note for ${status}:`);
    if (adminNote === null) return; // User cancelled
    
    setActionLoading(withdrawalId);
    try {
      const result = await dispatch(updateWithdrawalStatus({ withdrawalId, status, adminNote }) as any);
      if (result.payload?.success) {
        toast.success(`Withdrawal ${status} successfully`);
        dispatch(fetchWithdrawals({ page: pagination.page, limit: 10 }) as any);
      } else {
        toast.error(`Failed to ${status} withdrawal`);
      }
    } catch (error) {
      toast.error(`An error occurred while ${status}ing withdrawal`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(fetchWithdrawals({ page: 1, limit: 10 }) as any);
    setSearchTerm('');
    setStatusFilter('');
    setBankTypeFilter('');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <ThumbUp />;
      case 'pending': return <Pending />;
      case 'rejected': return <ThumbDown />;
      case 'cancelled': return <Cancel />;
      default: return <AccountBalanceWallet />;
    }
  };

  const withdrawalStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const bankTypes = [
    { value: BankType.TELEBIRR, label: 'Telebirr' },
    { value: BankType.AMOLE, label: 'Amole' },
    { value: BankType.CBE, label: 'CBE' },
    { value: BankType.CBEBIRR, label: 'CBE Birr' },
    { value: BankType.ABYSSINIA, label: 'Abyssinia' },
    { value: BankType.AWASH, label: 'Awash' },
    { value: BankType.OTHER, label: 'Other' },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, mb: { xs: 1, sm: 2 } }}>
        Withdrawals Management
      </Typography>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalanceWallet sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.totalCount || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Withdrawals
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Pending sx={{ mr: 2, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.statusBreakdown?.pending?.count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ThumbUp sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.statusBreakdown?.approved?.count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Approved
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ThumbDown sx={{ mr: 2, color: 'error.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.statusBreakdown?.rejected?.count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Rejected
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Payment sx={{ mr: 2, color: 'info.main' }} />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.totalAmount || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalanceWallet sx={{ mr: 2, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.averageAmount || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Average Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
            <TextField
              fullWidth
              placeholder="Search by phone, description, or bank type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                {withdrawalStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
              <InputLabel>Bank Type</InputLabel>
              <Select
                value={bankTypeFilter}
                onChange={(e) => setBankTypeFilter(e.target.value)}
                label="Bank Type"
              >
                <MenuItem value="">All Banks</MenuItem>
                {bankTypes.map((bank) => (
                  <MenuItem key={bank.value} value={bank.value}>
                    {bank.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Clear
              </Button>
            </Box>
            
            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterList />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                More Filters
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Withdrawals Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table sx={{ minWidth: { xs: 520, md: 800 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>User</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Bank Type</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Phone</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Account</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.map((withdrawal) => (
                <TableRow key={withdrawal.id}>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {withdrawal.user?.username || 'Unknown User'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {withdrawal.user?.telegramUsername || 'No Telegram'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography fontWeight="bold" color="error.main">
                        -{formatCurrency(withdrawal.amount)}
                      </Typography>
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={withdrawal.bankType}
                          size="small"
                          color="primary"
                        />
                        <Typography variant="caption" color="textSecondary">
                          {withdrawal.phoneNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Chip
                      label={withdrawal.bankType}
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="body2">
                      {withdrawal.phoneNumber}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="body2">
                      {withdrawal.accountNumber || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        icon={getStatusIcon(withdrawal.status)}
                        label={withdrawal.status}
                        color={getStatusColor(withdrawal.status) as any}
                        size="small"
                      />
                      <Typography variant="caption" color="textSecondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                        {dayjs(withdrawal.createdAt).format('MMM DD, HH:mm')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">
                      {dayjs(withdrawal.createdAt).format('MMM DD, YYYY HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewWithdrawal(withdrawal)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {withdrawal.status === 'pending' && (
                        <>
                          <Tooltip title="Approve Withdrawal">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleUpdateStatus(withdrawal.id, 'approved')}
                              disabled={actionLoading === withdrawal.id}
                            >
                              {actionLoading === withdrawal.id ? <CircularProgress size={16} /> : <ThumbUp />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject Withdrawal">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleUpdateStatus(withdrawal.id, 'rejected')}
                              disabled={actionLoading === withdrawal.id}
                            >
                              {actionLoading === withdrawal.id ? <CircularProgress size={16} /> : <ThumbDown />}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Withdrawal Details Modal */}
      <Dialog open={showWithdrawalDetails} onClose={() => setShowWithdrawalDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Withdrawal Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedWithdrawal && (
            <Box>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Basic Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Withdrawal ID</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedWithdrawal.id}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                      <Chip
                        icon={getStatusIcon(selectedWithdrawal.status)}
                        label={selectedWithdrawal.status}
                        color={getStatusColor(selectedWithdrawal.status) as any}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                      <Typography variant="body1" fontWeight="bold" color="error.main">
                        -{formatCurrency(selectedWithdrawal.amount)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Bank Type</Typography>
                      <Chip label={selectedWithdrawal.bankType} size="small" color="primary" />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">User Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Username</Typography>
                      <Typography variant="body1">{selectedWithdrawal.user?.username || 'Unknown User'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Phone Number</Typography>
                      <Typography variant="body1">{selectedWithdrawal.phoneNumber}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Account Number</Typography>
                      <Typography variant="body1">{selectedWithdrawal.accountNumber || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Telegram Username</Typography>
                      <Typography variant="body1">{selectedWithdrawal.user?.telegramUsername || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Telegram ID</Typography>
                      <Typography variant="body1">{selectedWithdrawal.user?.telegramId || 'N/A'}</Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Processing Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                      <Typography variant="body1">{selectedWithdrawal.description}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Admin Note</Typography>
                      <Typography variant="body1">{selectedWithdrawal.adminNote || 'No admin note'}</Typography>
                    </Box>
                    {selectedWithdrawal.processedBy && (
                      <>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">Processed By</Typography>
                          <Typography variant="body1">{selectedWithdrawal.processedBy?.username || 'Unknown'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">Processed At</Typography>
                          <Typography variant="body1">
                            {selectedWithdrawal.processedAt ? dayjs(selectedWithdrawal.processedAt).format('MMM DD, YYYY HH:mm:ss') : 'Not processed'}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Timestamps</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                      <Typography variant="body1">
                        {dayjs(selectedWithdrawal.createdAt).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWithdrawalDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WithdrawalsPage; 